import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';
import {
  ChartNoAxesColumnIcon,
  EyeIcon,
  ImagesIcon,
  SettingsIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  ORGANISATION_EDIT_PARAM,
  OrganisationEditDrawer,
} from '@modules/organisations/OrganisationEditDrawer';
import { OrganisationImages } from '@modules/organisations/OrganisationImages';
import { OrganisationMonitoring } from '@modules/organisations/OrganisationMonitoring';
import { OrganisationOverview } from '@modules/organisations/OrganisationOverview';
import { OrganisationSettings } from '@modules/organisations/OrganisationSettings';
import {
  DELETE_ORGANISATION,
  GET_ORGANISATION,
} from '@modules/organisations/organisationsQueries';
import { useLocale } from '@shared/hooks/locale';
import { isRATenantId } from '@shared/utils/tenant';

export const OrganisationDrawer = () => {
  const navigate = useNavigateWithSearchParams();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { organisationId } = useParams();
  const { me } = useAppData();

  const currentTab =
    useMatch('/organisations/:organisationId/:tab/*')?.params.tab ?? 'overview';

  const { data, error } = useQuery(GET_ORGANISATION, {
    variables: { id: organisationId ?? '' },
    skip: organisationId == null,
  });

  const [deleteOrganisation, { loading: isDeletingOrganisation }] = useMutation(
    DELETE_ORGANISATION,
    {
      onError: setErrorLike,
    },
  );

  const handleClose = () => navigate('../');

  const showMonitoringTab =
    me?.is_admin === true && isRATenantId(me.tenant_id ?? '');

  const tabsData: [string, string, React.ReactElement?][] = [
    [t('Overview'), 'overview', <EyeIcon />],
    ...(showMonitoringTab
      ? ([[t('Analytics'), 'monitoring', <ChartNoAxesColumnIcon />]] as [
          string,
          string,
          React.ReactElement?,
        ][])
      : []),
    [t('Images'), 'images', <ImagesIcon />],
    [t('Settings'), 'settings', <SettingsIcon />],
  ];

  const drawerActions = [
    {
      label: t('Delete organisation'),
      disabled: false,
      onClick: () => {
        if (organisationId == null) {
          return;
        }

        if (
          window.confirm(
            t('Are you sure you want to delete this organisation?'),
          )
        ) {
          // Delete organisation and remove from cache
          deleteOrganisation({
            variables: {
              id: organisationId,
            },
            update: cache => {
              cache.evict({ id: `organisations:${organisationId}` });
            },
          }).then(() => {
            // Close drawer
            handleClose();
          });
        }
      },
      icon: <Trash2Icon size={20} />,
    },
  ];

  const tabs: DrawerTab[] = tabsData.map(([label, value, icon]) =>
    generateTab([searchParams.toString(), label, value, icon]),
  );

  return (
    <Drawer
      onClose={handleClose}
      title={`${t('Organisation')} / ${data?.organisations_by_pk?.name}`}
      currentTab={currentTab}
      actions={drawerActions}
      actionsLoading={isDeletingOrganisation}
      maxWidth="none"
      tabs={tabs}
    >
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : (
        <Routes>
          <Route
            index
            element={
              <Navigate
                replace
                to={{
                  pathname: './overview',
                  search: searchParams.toString(),
                }}
              />
            }
          />
          <Route
            path="overview"
            element={
              <OrganisationOverview organisationId={organisationId ?? ''} />
            }
          />
          <Route
            path="monitoring"
            element={
              showMonitoringTab ? (
                <OrganisationMonitoring organisationId={organisationId ?? ''} />
              ) : (
                <Navigate
                  replace
                  to={{
                    pathname: '../overview',
                    search: searchParams.toString(),
                  }}
                />
              )
            }
          />
          <Route
            path="images"
            element={
              <OrganisationImages organisationId={organisationId ?? ''} />
            }
          />
          <Route
            path="settings"
            element={
              <OrganisationSettings organisationId={organisationId ?? ''} />
            }
          />
        </Routes>
      )}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
      {searchParams.get(ORGANISATION_EDIT_PARAM) != null &&
        organisationId != null && (
          <OrganisationEditDrawer organisationId={organisationId} />
        )}
    </Drawer>
  );
};
